import React, { useState, useEffect } from 'react';
import { FiDollarSign, FiTrendingUp, FiAlertCircle, FiShare2, FiCheck, FiPlus, FiTrash2, FiEdit2, FiDownload, FiX, FiSettings } from 'react-icons/fi';
import BoardFinanceSettingsModal from './BoardFinanceSettingsModal';
import './BoardFinance.css';

const CURRENCIES = ['USD', 'EUR', 'TRY', 'AED', 'SAR', 'GBP', 'JPY', 'CNY', 'INR'];

const formatMoney = (amount, currency = 'USD') => {
  const v = Number(amount || 0);
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(v);
  } catch (_e) {
    return `${v.toFixed(2)} ${currency}`;
  }
};

const todayStr = () => new Date().toISOString().slice(0, 10);

const PaymentForm = ({ initial, boardBaseCurrency, onSave, onCancel }) => {
  const [form, setForm] = useState(initial || {
    amount: '', currency: boardBaseCurrency, exchangeRate: 1, date: todayStr(), receivedFrom: '', note: '',
  });
  const baseEquivalent = (() => {
    const v = parseFloat(form.amount);
    const r = parseFloat(form.exchangeRate);
    if (!v || !r) return null;
    if (form.currency === boardBaseCurrency) return v;
    return v / r;
  })();

  return (
    <div className="bf-payment-form" data-testid="project-payment-form">
      <div className="bf-pf-grid">
        <div>
          <label className="form-label">Amount *</label>
          <input
            type="number" step="0.01"
            className="input" value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            placeholder="0.00"
            data-testid="project-payment-amount"
          />
        </div>
        <div>
          <label className="form-label">Currency</label>
          <select className="input" value={form.currency}
            onChange={(e) => {
              const next = e.target.value;
              setForm({ ...form, currency: next, exchangeRate: next === boardBaseCurrency ? 1 : form.exchangeRate });
            }}>
            {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            {!CURRENCIES.includes(form.currency) && <option value={form.currency}>{form.currency}</option>}
          </select>
        </div>
        <div>
          <label className="form-label">
            {form.currency === boardBaseCurrency ? 'Rate (same)' : `1 ${boardBaseCurrency} = ? ${form.currency}`}
          </label>
          <input
            type="number" step="0.0001" className="input" value={form.exchangeRate}
            onChange={(e) => setForm({ ...form, exchangeRate: e.target.value })}
            disabled={form.currency === boardBaseCurrency}
          />
        </div>
        <div>
          <label className="form-label">Date</label>
          <input type="date" className="input" value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
          />
        </div>
      </div>
      {baseEquivalent !== null && form.currency !== boardBaseCurrency && (
        <p className="form-helper" style={{ margin: '-8px 0 0 0' }}>
          ≈ <strong>{formatMoney(baseEquivalent, boardBaseCurrency)}</strong>
        </p>
      )}
      <div className="form-group">
        <label className="form-label">Received From</label>
        <input type="text" className="input" value={form.receivedFrom}
          onChange={(e) => setForm({ ...form, receivedFrom: e.target.value })}
          placeholder="Client name (defaults to project client)"
        />
      </div>
      <div className="form-group">
        <label className="form-label">Note</label>
        <input type="text" className="input" value={form.note}
          onChange={(e) => setForm({ ...form, note: e.target.value })}
          placeholder="e.g. Milestone 1 — 30% advance"
        />
      </div>
      <div className="bf-pf-actions">
        <button className="btn btn-secondary btn-sm" onClick={onCancel}>
          <FiX size={14} /> Cancel
        </button>
        <button className="btn btn-primary btn-sm" onClick={() => onSave(form)} data-testid="save-project-payment">
          <FiCheck size={14} /> Save Payment
        </button>
      </div>
    </div>
  );
};

const BoardFinance = ({ boardId, board, token, onTaskClick, onBoardUpdate, refreshKey = 0 }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareLink, setShareLink] = useState('');
  const [copiedLink, setCopiedLink] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  const apiBase = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/boards/${boardId}/finance-summary`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setSummary(data);
      }
    } catch (e) {
      console.error('Failed to fetch finance summary:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchClientShareLink = async () => {
    try {
      const res = await fetch(`${apiBase}/invites`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: 'shared-link@finance.view',
          projectIds: [boardId],
          message: 'Finance view-only link',
        }),
      });
      if (res.ok) {
        const data = await res.json();
        const t = data.invite?.token || data.token;
        if (t) setShareLink(`${window.location.origin}/client-portal/${t}`);
      }
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchSummary(); /* eslint-disable-next-line */ }, [boardId, refreshKey]);

  const copyLink = async () => {
    if (!shareLink) return;
    try { await navigator.clipboard.writeText(shareLink); setCopiedLink(true); }
    catch {
      const ta = document.createElement('textarea');
      ta.value = shareLink; document.body.appendChild(ta); ta.select();
      document.execCommand('copy'); document.body.removeChild(ta);
      setCopiedLink(true);
    }
    setTimeout(() => setCopiedLink(false), 2500);
  };

  const handleAddPayment = async (form) => {
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) return;
    try {
      await fetch(`${apiBase}/boards/${boardId}/project-payments`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          boardId,
          amount,
          currency: form.currency,
          exchangeRate: parseFloat(form.exchangeRate) || 1,
          date: form.date || todayStr(),
          receivedFrom: form.receivedFrom || '',
          note: form.note || '',
        }),
      });
      setShowAdd(false);
      fetchSummary();
    } catch (e) { console.error(e); }
  };

  const handleUpdatePayment = async (id, form) => {
    try {
      await fetch(`${apiBase}/project-payments/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: parseFloat(form.amount) || 0,
          currency: form.currency,
          exchangeRate: parseFloat(form.exchangeRate) || 1,
          date: form.date,
          receivedFrom: form.receivedFrom,
          note: form.note,
        }),
      });
      setEditingId(null);
      fetchSummary();
    } catch (e) { console.error(e); }
  };

  const handleDeletePayment = async (id) => {
    if (!window.confirm('Delete this payment?')) return;
    try {
      await fetch(`${apiBase}/project-payments/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      fetchSummary();
    } catch (e) { console.error(e); }
  };

  const downloadReceipt = async (paymentId, receiptNumber) => {
    try {
      const res = await fetch(`${apiBase}/project-payments/${paymentId}/receipt`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `receipt-${receiptNumber || paymentId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { console.error(e); }
  };

  if (loading) {
    return <div className="board-finance-loading"><div className="loading-spinner" /></div>;
  }
  if (!summary) return <div className="empty-state">No data available.</div>;

  const {
    baseCurrency, supervisionFeePercent, supervisionFeeVisible,
    tasksExpensesBase, supervisionFeeBase, projectCostBase, tasksExpensesDisplayedBase,
    totalReceivedBase, remainingBase, percentPaid,
    payments = [], items = [],
  } = summary;

  // For "hidden" mode, the cost breakdown shows distributed values per task
  // and no separate supervision fee line is presented to the client.
  const showAsHidden = !supervisionFeeVisible && supervisionFeePercent > 0;

  // Phase 1 (Part 6): if any board task carries linkedFinanceItemId, prefer the
  // client-visible amounts pushed from Finance instead of legacy "value".
  const itemsRendered = (items || []).map((it) => {
    if (it.linkedFinanceItemId && it.clientTotalAmount != null && it.clientTotalAmount > 0) {
      return {
        ...it,
        // Surface client-visible figures as the row's headline values.
        // Keep the original fields too so existing UI keeps working.
        valueDisplay: it.clientTotalAmount,
        clientVisible: it.clientVisibleAmount,
        supervision: it.supervisionAmount,
        isLinkedToFinance: true,
      };
    }
    return it;
  });

  // Phase 1: unified visibility filter -- include linked Finance rows even when
  // legacy `value` is 0, since they carry clientTotalAmount from Finance.
  const visibleFinanceItems = itemsRendered.filter((item) => {
    const amount = item.isLinkedToFinance
      ? Number(item.clientTotalAmount ?? item.valueDisplay ?? item.value ?? 0)
      : Number(item.value ?? item.valueDisplay ?? 0);
    return amount > 0;
  });

  return (
    <div className="board-finance" data-testid="board-finance">
      {/* Phase 1: explicit client-statement banner so the user knows this view
          is safe to open in front of the client. */}
      <div className="finance-client-banner" data-testid="client-statement-banner">
        <strong>Client Statement</strong>
        <span className="muted"> — safe to open in front of the client. Internal cost and profit are never shown here.</span>
      </div>
      {/* Settings button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => setShowSettings(true)}
          data-testid="finance-settings-btn"
        >
          <FiSettings size={14} /> Client Statement Settings
        </button>
      </div>

      {/* Top KPI cards */}
      <div className="finance-kpi-grid">
        <div className="finance-kpi-card finance-kpi-value">
          <div className="finance-kpi-icon"><FiDollarSign size={24} /></div>
          <div className="finance-kpi-content">
            <div className="finance-kpi-label">Project Total</div>
            <div className="finance-kpi-amount" data-testid="finance-project-cost">
              {formatMoney(projectCostBase, baseCurrency)}
            </div>
            <div className="finance-kpi-sub">
              {showAsHidden ? (
                <>Tasks (incl. {supervisionFeePercent}% fee distributed)</>
              ) : supervisionFeePercent > 0 ? (
                <>Tasks {formatMoney(tasksExpensesBase, baseCurrency)} + {supervisionFeePercent}% supervision</>
              ) : (
                <>Tasks {formatMoney(tasksExpensesBase, baseCurrency)}</>
              )}
            </div>
          </div>
        </div>
        <div className="finance-kpi-card finance-kpi-paid">
          <div className="finance-kpi-icon"><FiTrendingUp size={24} /></div>
          <div className="finance-kpi-content">
            <div className="finance-kpi-label">Total Received</div>
            <div className="finance-kpi-amount" data-testid="finance-total-received">
              {formatMoney(totalReceivedBase, baseCurrency)}
            </div>
            <div className="finance-kpi-sub">{(percentPaid || 0).toFixed(1)}% of project total</div>
          </div>
        </div>
        <div className="finance-kpi-card finance-kpi-remaining">
          <div className="finance-kpi-icon"><FiAlertCircle size={24} /></div>
          <div className="finance-kpi-content">
            <div className="finance-kpi-label">Remaining (Client Owes)</div>
            <div className="finance-kpi-amount" data-testid="finance-remaining">
              {formatMoney(remainingBase, baseCurrency)}
            </div>
            <div className="finance-kpi-sub">{remainingBase > 0 ? 'Outstanding' : 'Fully paid 🎉'}</div>
          </div>
        </div>
      </div>

      {/* Overall progress bar */}
      <div className="finance-progress-block">
        <div className="finance-progress-header">
          <span>Payment Progress</span>
          <span className="finance-progress-percent">{(percentPaid || 0).toFixed(0)}%</span>
        </div>
        <div className="finance-progress-track">
          <div className="finance-progress-fill" style={{ width: `${Math.min(100, percentPaid || 0)}%` }} />
        </div>
      </div>

      {/* Project Payments (Receipts) */}
      <div className="finance-section">
        <div className="finance-section-header">
          <h3 className="finance-section-title">
            <FiDollarSign size={18} />
            <span>Payments Received ({payments.length})</span>
          </h3>
          <div className="finance-share-actions">
            {!showAdd && editingId === null && (
              <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)} data-testid="add-project-payment-btn">
                <FiPlus size={14} /> Add Payment
              </button>
            )}
            {!shareLink ? (
              <button className="btn btn-secondary btn-sm" onClick={fetchClientShareLink} data-testid="generate-finance-share-link">
                <FiShare2 size={14} /> Share with Client
              </button>
            ) : (
              <div className="finance-share-link">
                <input type="text" readOnly value={shareLink} className="input" data-testid="finance-share-link-input" />
                <button className="btn btn-primary btn-sm" onClick={copyLink}>
                  {copiedLink ? <><FiCheck size={14} /> Copied</> : 'Copy'}
                </button>
              </div>
            )}
          </div>
        </div>

        {showAdd && (
          <PaymentForm
            boardBaseCurrency={baseCurrency}
            onSave={handleAddPayment}
            onCancel={() => setShowAdd(false)}
          />
        )}

        {payments.length === 0 && !showAdd && (
          <div className="empty-state">No payments received yet. Add the first receipt above.</div>
        )}

        {payments.length > 0 && (
          <div className="bf-payments-list">
            {payments.map((p) => (
              <div key={p.id} className="bf-payment-row" data-testid="project-payment-row">
                {editingId === p.id ? (
                  <PaymentForm
                    initial={{
                      amount: p.amount,
                      currency: p.currency,
                      exchangeRate: p.exchangeRate,
                      date: p.date,
                      receivedFrom: p.receivedFrom,
                      note: p.note,
                    }}
                    boardBaseCurrency={baseCurrency}
                    onSave={(form) => handleUpdatePayment(p.id, form)}
                    onCancel={() => setEditingId(null)}
                  />
                ) : (
                  <>
                    <div className="bf-pr-left">
                      <div className="bf-pr-amount">
                        {formatMoney(p.amount, p.currency)}
                        {p.currency !== baseCurrency && (
                          <span className="bf-pr-base">≈ {formatMoney(p.amountBase, baseCurrency)}</span>
                        )}
                      </div>
                      <div className="bf-pr-meta">
                        <span><strong>{p.receivedFrom || '—'}</strong></span>
                        <span>•</span>
                        <span>{p.date}</span>
                        <span>•</span>
                        <span className="bf-pr-rcpt">#{p.receiptNumber}</span>
                      </div>
                      {p.note && <div className="bf-pr-note">{p.note}</div>}
                    </div>
                    <div className="bf-pr-actions">
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => downloadReceipt(p.id, p.receiptNumber)}
                        title="Download PDF Receipt"
                        data-testid="download-receipt-btn"
                      >
                        <FiDownload size={14} /> Receipt
                      </button>
                      <button className="row-action-btn" onClick={() => setEditingId(p.id)} aria-label="Edit">
                        <FiEdit2 size={14} />
                      </button>
                      <button className="row-action-btn" onClick={() => handleDeletePayment(p.id)} aria-label="Delete">
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Per-task costs */}
      <div className="finance-section">
        <h3 className="finance-section-title">
          <FiDollarSign size={18} />
          <span>Client Statement Items ({visibleFinanceItems.length})</span>
        </h3>
        <p className="form-helper" style={{ marginTop: '-4px', marginBottom: 12 }}>
          These are the client-visible amounts for each task. Internal cost and profit are kept private.
          Click a row to record a client payment received.
        </p>
        {visibleFinanceItems.length === 0 ? (
          <div className="empty-state">No client items yet. Open a task → Finance tab → set a Client Amount, or push values from Quotations & Costing.</div>
        ) : (
          <div className="finance-table finance-table-expanded">
            <div className="finance-table-header finance-table-header-expanded">
              <div>Task</div>
              <div>Group</div>
              <div className="text-right">Client Amount</div>
              <div className="text-right">Paid</div>
              <div className="text-right">Remaining</div>
              <div className="text-right">Action</div>
            </div>
            {visibleFinanceItems.map((it) => {
              const paid = Number(it.totalPaid || 0);
              const remaining = Number(it.remaining || 0);
              const paidPct = Number(it.paidPercent || 0);
              const fullyPaid = paid > 0 && remaining <= 0.005;
              // Phase 1: use client-visible total when linked to Finance
              // CRITICAL: keep per-row display consistent with the Supervision
              // row at the bottom. If supervision is shown as its own line, every
              // task row must show the BASE value (it.value) — otherwise the
              // supervision is visually counted twice (once baked into each row,
              // once as the bottom row). The PROJECT TOTAL math is unchanged.
              const displayValue = (supervisionFeePercent > 0 && supervisionFeeVisible)
                ? it.value
                : ((it.isLinkedToFinance && it.valueDisplay != null) ? it.valueDisplay : it.value);
              return (
                <div
                  key={it.itemId}
                  className="finance-task-row-expanded"
                  onClick={() => onTaskClick && onTaskClick(it.itemId)}
                  data-testid="finance-task-row"
                  title={it.isLinkedToFinance ? 'Client-visible amount synced from Finance Project. Edit in Finance.' : 'Click to add or edit expenses for this task'}
                >
                  <div className="finance-task-name">
                    {it.name}
                    {it.isLinkedToFinance && (
                      <span className="finance-task-badge" title="Synced from Finance Project">Finance</span>
                    )}
                    {it.paymentsCount > 0 && (
                      <span className="finance-task-badge" data-testid="task-expense-count">
                        {it.paymentsCount} expense{it.paymentsCount === 1 ? '' : 's'}
                      </span>
                    )}
                  </div>
                  <div className="finance-task-group">{it.groupName}</div>
                  <div className="text-right">{formatMoney(displayValue, it.currency)}</div>
                  <div className="text-right" style={{ color: paid > 0 ? '#1DBF73' : 'var(--text-secondary)' }}>
                    {formatMoney(paid, it.currency)}
                    {it.value > 0 && (
                      <div className="finance-task-paid-bar">
                        <div
                          className="finance-task-paid-fill"
                          style={{ width: `${Math.min(100, paidPct)}%` }}
                        />
                      </div>
                    )}
                  </div>
                  <div className="text-right" style={{ color: fullyPaid ? '#1DBF73' : remaining > 0 ? '#E24B5B' : 'var(--text-secondary)' }}>
                    <strong>{formatMoney(remaining, it.currency)}</strong>
                    {fullyPaid && <div className="finance-task-paid-label">Fully paid</div>}
                  </div>
                  <div className="text-right">
                    <button
                      type="button"
                      className="btn btn-secondary btn-xs"
                      onClick={(e) => { e.stopPropagation(); if (onTaskClick) onTaskClick(it.itemId); }}
                      data-testid="add-task-expense-btn"
                    >
                      <FiPlus size={12} /> Expense
                    </button>
                  </div>
                </div>
              );
            })}
            {supervisionFeePercent > 0 && supervisionFeeVisible && (
              <div className="finance-task-row-expanded finance-subtotal-row">
                <div className="finance-task-name"><strong>Subtotal (tasks)</strong></div>
                <div className="finance-task-group">—</div>
                <div className="text-right">
                  <strong>{formatMoney(tasksExpensesBase, baseCurrency)}</strong>
                </div>
                <div className="text-right">—</div>
                <div className="text-right">—</div>
                <div className="text-right">—</div>
              </div>
            )}
            {supervisionFeePercent > 0 && supervisionFeeVisible && (
              <div className="finance-task-row-expanded finance-supervision-row">
                <div className="finance-task-name"><em>Supervision Fee ({supervisionFeePercent}%)</em></div>
                <div className="finance-task-group">—</div>
                <div className="text-right">
                  <strong>+ {formatMoney(supervisionFeeBase, baseCurrency)}</strong>
                </div>
                <div className="text-right">—</div>
                <div className="text-right">
                  <strong>+ {formatMoney(supervisionFeeBase, baseCurrency)}</strong>
                </div>
                <div className="text-right">—</div>
              </div>
            )}
            <div className="finance-task-row-expanded finance-total-row">
              <div className="finance-task-name"><strong>PROJECT TOTAL</strong></div>
              <div className="finance-task-group">—</div>
              <div className="text-right">
                <strong style={{ color: 'var(--monday-blue)' }}>{formatMoney(projectCostBase, baseCurrency)}</strong>
              </div>
              <div className="text-right">—</div>
              <div className="text-right">
                <strong style={{ color: 'var(--monday-blue)' }}>{formatMoney(projectCostBase, baseCurrency)}</strong>
              </div>
              <div className="text-right">—</div>
            </div>
          </div>
        )}
      </div>

      <BoardFinanceSettingsModal
        isOpen={showSettings}
        board={board}
        token={token}
        onClose={() => setShowSettings(false)}
        onUpdate={() => {
          fetchSummary();
          if (onBoardUpdate) onBoardUpdate();
        }}
      />
    </div>
  );
};

export default BoardFinance;
